import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { Wrapper } from "@googlemaps/react-wrapper";
import React, { useEffect, useRef, useState } from "react";
import { renderToString } from "react-dom/server";
import { BusDelayed, BusPosition } from "../../../assets/iconsMapPNG/_index";
import * as Icon1 from "../../../assets/iconsMapPNG/incidents/priority_1/_index";
import Theme from '../../../styles/Theme/Theme';
import { mapStyles } from "../../../utils/data/mapStyle";
import MouseoverTooltip from "../Atomics/MouseoverTooltip";
import { IconBase64 } from "@synergy/icons";

/**
 * Returns videowall-mode flag and derived sizing values.
 * Module-level function so it can be called from both the MyMap component
 * and the standalone incidentsLayer / realTimeVehicle functions.
 */
function getVideowallConfig() {
    const isVideowall =
        document.documentElement.dataset.displayMode === 'videowall' ||
        document.querySelector('[data-display-mode="videowall"]') !== null ||
        window.matchMedia(Theme.devices.videowall).matches;
    return {
        isVideowall,
        marginTooltipWrapper:   isVideowall ? Theme.spacing.xl : Theme.spacing.md,
        fontSizeTooltipTitle:   isVideowall ? '27px' : Theme.typography.fontSize['text_xs_desktop'],
        fontWeightTooltipTitle: Theme.typography.weight.medium,
        fontSizeTooltipText:    isVideowall ? '21px' : Theme.typography.fontSize['text_xs_desktop'],
    };
}

//CREATE MAP
const MyMap = (props) => {
    let [map, setMap] = useState();
    const ref = useRef();
    const trafficLayerRef = useRef(null);
    const { isVideowall, marginTooltipWrapper, fontSizeTooltipTitle, fontWeightTooltipTitle, fontSizeTooltipText } = getVideowallConfig();
    const windowWidth = window.innerWidth;

    const data = props?.config?.configurations;
    const incidents = Array.isArray(props?.config?.incidents) ? props?.config?.incidents : [];
    const gtfsRealtime = props?.config?.realtimebus ?? [];

    const cascaisBounds = {
        north: 38.7900,
        south: 38.6000,
        west: -9.6000,
        east: -9.2000,
    };

    const mapConfig = {
        mapId: data.mapId ?? null,
        center: { lat: data.lat ?? 38.715357, lng: data.lng ?? -9.360761 },
        zoom: data.zoom ?? 13,
        disableDefaultUI: data.disableDefaultUI ?? true,
        clickableIcons: false,
        restriction: {
            latLngBounds: cascaisBounds,
            strictBounds: false,
        },
        heading: data.heading ?? 0,
        tilt: data.tilt ?? 60,
        styles: !data.mapId ? (Array.isArray(data.styles) ? data.styles : mapStyles[data.styles] ?? "default") : null,
    }

    //Layers
    const showTransit = data.showTransit ?? false;
    const showTraffic = data.showTraffic ?? false;
    const showBusRoutes = data.showBusRoutes ?? false;
    const showIncidents = data.showIncidents ?? false;
    const showIncidentsTooltips = data.showIncidentsTooltips ?? false;
    const showRealTimeVehicle = data.showRealTimeVehicle ?? false;
    const showRealTimeVehicleTooltip = data.showRealTimeVehicleTooltip ?? false;

    useEffect(() => {
        if (ref.current && !map) {
            map = new window.google.maps.Map(ref.current, mapConfig);
            trafficLayerRef.current = new window.google.maps.TrafficLayer();
        }
    }, [ref, map]);

    // Incidents layer — re-runs when data OR visibility flag changes
    useEffect(() => {
        setMap(null);
        incidentsLayer(showIncidents ? incidents : [], map, showIncidentsTooltips);
        setMap(map);
    }, [incidents, showIncidents])

    // Real-time vehicle layer — re-runs when data OR visibility flag changes
    useEffect(() => {
        setMap(null);
        realTimeVehicle(showRealTimeVehicle ? gtfsRealtime : [], map, showRealTimeVehicleTooltip);
        setMap(map);
    }, [gtfsRealtime, showRealTimeVehicle])

    // Traffic layer — toggle on/off without recreating the entire map
    useEffect(() => {
        if (!trafficLayerRef.current || !map) return;
        trafficLayerRef.current.setMap(showTraffic ? map : null);
    }, [showTraffic, map])

    return <div ref={ref} id="map" style={{ overflow: "visible!important", height: "100%", borderRadius: 'inherit' }}></div>
}

function updateTrafficOnMap(map, trafficLayer, on) {
    if (on == 0) {
        trafficLayer.setMap(null);
        setTimeout(function () { updateTrafficOnMap(map, null, 1) }, 1)
    }
    if (on == 1) {
        var trafficLayer2 = new window.google.maps.TrafficLayer();
        trafficLayer2.setMap(map);
        // after 300ms (or 5 minutes) update the traffic map
        setTimeout(function () { updateTrafficOnMap(map, trafficLayer2, 0) }, 300000)
    }
}

//Incidents Layer
let markers = [];
let markerClusterer = null;
const incidentsLayer = (incidents, map, showIncidentsTooltips) => {
    const { isVideowall, fontSizeTooltipText } = getVideowallConfig();
    // const highPriority = [];
    // const mediumPriority = [];

    markers.map(e => {
        window.google.maps.event.clearListeners(e, 'click');
        window.google.maps.event.clearListeners(e, 'closeclick');
        e.setMap(null);
    })

    markers = [];
    //garante a eliminação completa dos clusters antes de criar um novo, algum error verificar aqui!
    if (markerClusterer) {
        markerClusterer.clearMarkers();
        markerClusterer.setMap(null);
        markerClusterer = null;
    }

    if (incidents.length) {
        console.log(`Displaying ${incidents.length} incidents on map`);
        incidents.filter(i => i.priority === "1 - High" || i.priority === "2 - Medium High").map((e) => {
            const latLng = { lat: parseFloat(e.latitude), lng: parseFloat(e.longitude) };

            const iconBase64 = IconBase64({
                class: e.class,
                family: e.family,
                label: e.label || 'Incident', // Default to 'Incident' if label not present
                priority: e.priority
            });

            const scaledSize = !isVideowall ? new window.google.maps.Size(30, 30) : new window.google.maps.Size(40, 40);
            let iconToShow = {
                url: iconBase64, 
                scaledSize: scaledSize
            }

            const marker = new window.google.maps.Marker({
                position: latLng,
                map,
                title: e.affected_user ?? null,
                icon: iconToShow,
                data: e,
            });

            /**
             * Tooltip data
             */
            let mouseoverTooltipData = {
                ...e,
                fields: [
                    { fieldName: 'Class', value: e.class ?? 'N/A' },
                    { fieldName: 'Name', value: e.name ?? 'N/A' }
                ],
                gridColumns: 2
            };

            const content = renderToString(<MouseoverTooltip data={[mouseoverTooltipData]} />);
            const infoWindow = new window.google.maps.InfoWindow({
                content: content,
                position: latLng,
                maxWidth: 1000,
                disableAutoPan: true
            });
            marker.addListener("click", () => {
                infoWindow.open({
                    map: map,
                    anchor: marker,
                    shouldFocus: true
                });
            });
            map.addListener("click", () => {
                infoWindow.close();
            });

            marker.setClickable(showIncidentsTooltips)

            markers.push(marker);
        })
    }

    if (markers.length > 0) {
        let renderer = {
            gridSize: 60, // Ajuste o tamanho da grade conforme necessário
            minimumClusterSize: 20, // Ajuste o número mínimo de marcadores para formar um cluster
            maxZoom: 30, // Ajuste o nível de zoom máximo conforme necessário
            render({ count, position, markers }, stats) {
                const highestPriority = markers.reduce((highest, marker) => {
                    const priority = parseInt(marker.data.priority.split(' - ')[0], 10);
                    return priority < highest ? priority : highest;
                }, Infinity);

                const svg = window.btoa(`
                <svg width="64" height="64" viewBox="0 0 152 152" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="4" y="4" width="148" height="148" rx="50%" fill="${highestPriority === 1 ? '#FF0000' : '#0076A8'}"/>
                </svg>`);

                const scaledSize = !isVideowall ? new window.google.maps.Size(30, 30) : new window.google.maps.Size(60, 60);
                const clusterMarker = new window.google.maps.Marker({
                    position,
                    icon: {
                        url: `data:image/svg+xml;base64,${svg}`,
                        scaledSize: scaledSize
                    },
                    title: `Cluster de ${count} marcadores`,
                    zIndex: Number(window.google.maps.Marker.MAX_ZINDEX) + count,
                    label: {
                        text: String(count),
                        color: "rgba(255,255,255,0.9)",
                        fontSize: fontSizeTooltipText,
                    },
                    clickable: true,  // Permite o clique no cluster
                });

                let mouseoverClusterTooltipData = markers.map(e => {
                    return {
                        ...e.data,
                        fields: [
                            { fieldName: 'Class', value: e.data.class ?? 'N/A' },
                            { fieldName: 'Name', value: e.data.name ?? 'N/A' }
                        ],
                        gridColumns: 2
                    };
                });

                const tooltipContent = renderToString(<MouseoverTooltip data={mouseoverClusterTooltipData} />);

                // Criando a InfoWindow (tooltip) para exibir ao clicar no cluster
                const infoWindow = new window.google.maps.InfoWindow({
                    content: tooltipContent,
                    position: position,
                    maxWidth: 300,
                    disableAutoPan: true
                });

                // Evento de clique no cluster para abrir a InfoWindow
                clusterMarker.addListener('click', () => {
                    // Apenas abrir a InfoWindow sem fazer zoom
                    infoWindow.open({
                        map: map, // Mapa onde a InfoWindow será aberta
                        anchor: clusterMarker, // O marcador do cluster é o ponto de ancoragem
                        shouldFocus: false
                    });
                });

                return clusterMarker;
            },
            onClick: (cluster) => {
                // Previne o comportamento padrão de abrir o cluster (zoom)
                const markers = cluster.getMarkers();
                console.log("Cluster clicked. Markers:", markers);
                // Não faz nada aqui para evitar o zoom
            },
        };

        markerClusterer = new MarkerClusterer({
            markers,
            map,
            renderer,
            onClusterClick: (cluster) => {
            },
            algorithmOptions: {
                maxZoom: 22,
            }
        });

        markers.map(e => {
            e.setMap(map);
        });
    } else {
        console.log('No incidents to display - map cleared');
    }
};

let busMarkers = [];
//Real time vehicle
const realTimeVehicle = (gtfsRealtime, map, showRealTimeVehicleTooltip) => {
    const { isVideowall, marginTooltipWrapper, fontSizeTooltipTitle, fontWeightTooltipTitle } = getVideowallConfig();

    busMarkers.map(e => {
        window.google.maps.event.clearListeners(e, 'click');
        window.google.maps.event.clearListeners(e, 'closeclick');
        e.setMap(null);
    })

    busMarkers = [];

    let identities = gtfsRealtime?.entity;

    if (identities) {
        identities.map((e) => {

            const isIncident = false;

            let newLat = e.vehicle.position.latitude;
            let newLng = e.vehicle.position.longitude;

            const latLng = { lat: parseFloat(newLat), lng: parseFloat(newLng) };

            // const scaledSize = !isVideowall ? new window.google.maps.Size(25, 25) : new window.google.maps.Size(60, 60);

            // let labelId = e?.id.split(':');
            // let labelText = e?.vehicle?.trip?.routeId ? e?.vehicle?.trip?.routeId + ': ' + labelId[0] : labelId[0];
            // let textWidth = labelText.toString().length;
            // let getFont = Theme.typography.fontSize[fontSizeTooltipText];
            // const numericValue = parseFloat(fontSizeTooltipText.match(/[\d\.]+/)[0]);
            // let fontSizeLabel = numericValue * windowWidth / 100
            // let widthLabel = textWidth * fontSizeLabel * 0.7;
            // let heightLabel = fontSizeLabel * 1.5;

            let iconToShow;
            if (isIncident) {
                iconToShow = {
                    url: Icon1['transportation'],
                    scaledSize: !isVideowall ? new window.google.maps.Size(25, 25) : new window.google.maps.Size(25, 25)
                }
            } else if (e?.vehicle?.trip) {
                iconToShow = {
                    url: BusPosition,
                    scaledSize: !isVideowall ? new window.google.maps.Size(25, 25) : new window.google.maps.Size(25, 25)
                }
            } else {
                iconToShow = {
                    url: BusDelayed,
                    scaledSize: !isVideowall ? new window.google.maps.Size(25, 25) : new window.google.maps.Size(25, 25)
                }
            }


            let contentText = '<div style="margin: ' + marginTooltipWrapper + '"><p style="font-size: ' + fontSizeTooltipTitle + '; font-weight: ' + fontWeightTooltipTitle + '; margin:0" >' + e?.vehicle?.trip?.routeId + '</p></div>';

            const marker = new window.google.maps.Marker({
                position: latLng,
                map,
                icon: iconToShow,
            });

            const infoWindow = new window.google.maps.InfoWindow({
                content: contentText,
                position: latLng,
                maxWidth: 1000,
                disableAutoPan: true
            });

            marker.addListener("click", () => {
                infoWindow.open({
                    map: map,
                    anchor: marker,
                    shouldFocus: false
                });
            });

            infoWindow.addListener('click', () => {
                infoWindow.close();
            })

            marker.setClickable(showRealTimeVehicleTooltip)

            busMarkers.push(marker);

        })
    }

    busMarkers.map(e => {
        e.setMap(map);
    })

}

//RENDER MAP
const GoogleMap = (props) => {
    return (
        <Wrapper apiKey={process.env.REACT_APP_MAPS_API_KEY} ><MyMap config={props} /></Wrapper>
    )
}

export { GoogleMap };
